import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Table,
  Collapse,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  IconButton,
  Typography,
} from '@mui/material';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SecondStepFormItem } from '../../types/billing-form.types';
import { DistributionValueInput } from '../input/distribution-value-input';
import { MilestoneDetailRow } from './milestone-detail-row';
import { RHFNumberField } from '@components/forms/hook-form';

interface DistributionItemsTableProps {
  formValueIndex: number;
  items: SecondStepFormItem[];
}

export const DistributionItemsTable = ({ items, formValueIndex }: DistributionItemsTableProps) => {
  const { t } = useTranslation();
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  const toggleExpand = (subscription: string, type: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [`${subscription}-${type}`]: !prev[`${subscription}-${type}`],
    }));
  };

  return (
    <Table stickyHeader aria-label="legal entities table" sx={{ tableLayout: 'fixed' }}>
      <colgroup>
        <col style={{ width: '40%' }} />
        <col style={{ width: '10%' }} />
        <col style={{ width: '25%' }} />
        <col style={{ width: '25%' }} />
      </colgroup>
      <TableHead>
        <TableRow>
          <TableCell>{t('Subscription')}</TableCell>
          <TableCell align="center" />
          <TableCell align="center">{t('Quantity')}</TableCell>
          <TableCell align="center">{t('Amount')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((item, itemIndex) => {
          const isExpanded = expandedItems[`${item.itemInfo.subscription}-${item.itemInfo.type}`];
          const { amount } = item;
          return (
            <Fragment
              key={`distribution-item-row-${item.itemInfo.subscription}-${item.itemInfo.type}`}
            >
              <TableRow>
                <TableCell>
                  <Typography variant="subtitle2">
                    {item.itemInfo.productName} - {item.itemInfo.sku} - {item.itemInfo.plan.name}
                  </Typography>
                </TableCell>

                <TableCell>
                  <IconButton
                    onClick={() => toggleExpand(item.itemInfo.subscription, item.itemInfo.type)}
                  >
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </TableCell>

                <TableCell align="center">
                  <DistributionValueInput
                    formValueIndex={formValueIndex}
                    item={item}
                    itemIndex={itemIndex}
                  />
                </TableCell>

                <TableCell align="center">
                  <RHFNumberField
                    name={`formValues[${formValueIndex}].items[${itemIndex}].amount`}
                    disabled
                    thousandsGroupStyle="thousand"
                    thousandSeparator
                    helperText=" "
                    label={t('Amount to bill')}
                    decimalScale={2}
                    prefix={'$'}
                    sx={{ width: '60%' }}
                    size="small"
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={4} style={{ padding: 0, border: 0 }}>
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    {item.itemInfo.milestones.map((milestone) => (
                      <MilestoneDetailRow
                        key={`milestone-row-${milestone._id}`}
                        milestone={milestone}
                      />
                    ))}
                  </Collapse>
                </TableCell>
              </TableRow>
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
};
